"use client";

import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users,
  Search as SearchIcon,
  LayoutGrid,
  List,
  Plus,
} from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { getArtists, type ArtistProfile } from "@/services/artists";
import { cn } from "@/lib/utils";
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar";
import { isPlaceholderImage } from "@/lib/image-helpers";
import { AdminEditOverlay } from "@/components/admin/admin-quick-edit-dialog";
import { useAuth } from "@/contexts/auth-context";
import { useUrlParams } from "@/hooks/use-url-params";
import { SlideshowImage } from "@/components/ui/slideshow-image";

/** Primary photo first, then gallery extras (images/GIFs/videos), deduped —
 *  used to feed the card thumbnail slideshow (Gordon 2026-09-20: "thumbnails
 *  should autoplay ... between images, gifs, and mp4's"). A single-item
 *  array degrades to a plain static image automatically (SlideshowImage). */
function cardSlides(artist: Pick<ArtistProfile, "imageUrl" | "images">): string[] {
  const extra = (artist.images || []).filter(Boolean);
  const primary = artist.imageUrl && !isPlaceholderImage(artist.imageUrl) ? [artist.imageUrl] : [];
  return [...primary, ...extra.filter((u) => !primary.includes(u))];
}

// ─── Types ─────────────────────────────────────────────────────────────────────

type ViewMode = "grid" | "list";
type SortMode = "az" | "za" | "newest" | "oldest";

// ─── Helpers ───────────────────────────────────────────────────────────────────

function sortArtists(artists: ArtistProfile[], sort: SortMode): ArtistProfile[] {
  return [...artists].sort((a, b) => {
    if (sort === "az")      return (a.name || "").localeCompare(b.name || "");
    if (sort === "za")      return (b.name || "").localeCompare(a.name || "");
    // newest / oldest: by dateAdded; undated artists last either way
    const da = a.dateAdded ? new Date(a.dateAdded).getTime() : NaN;
    const db = b.dateAdded ? new Date(b.dateAdded).getTime() : NaN;
    if (Number.isNaN(da) || Number.isNaN(db)) return Number.isNaN(da) ? (Number.isNaN(db) ? 0 : 1) : -1;
    return sort === "oldest" ? da - db : db - da;
  });
}

function getInitials(name: string): string {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join("");
}

// ─── Artist Card ────────────────────────────────────────────────────────────────

function ArtistCard({
  artist,
  viewMode,
}: {
  artist: ArtistProfile;
  viewMode: ViewMode;
}) {
  const href = `/artists/${artist.slug || artist.id}`;
  const initials = getInitials(artist.name);
  const slides = cardSlides(artist);
  const slideshowProps = {
    interval: artist.slideshowSettings?.intervalSec ? artist.slideshowSettings.intervalSec * 1000 : undefined,
    fadeMs: artist.slideshowSettings?.fadeSec ? artist.slideshowSettings.fadeSec * 1000 : undefined,
    transition: artist.slideshowSettings?.transition as any,
  };

  if (viewMode === "list") {
    return (
      <Link
        href={href}
        className="flex gap-4 items-center p-3 rounded-xl border border-border/50 bg-card hover:bg-card/80 transition-all hover:border-border group"
      >
        {/* Thumbnail */}
        <div className="relative w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
          {slides.length > 0 ? (
            <SlideshowImage images={slides} alt={artist.name} className="group-hover:scale-105 transition-transform duration-300" {...slideshowProps} />
          ) : (
            <GenerativeArtistAvatar name={artist.name} size={56} className="w-full h-full" animate />
          )}
        </div>

        {/* Info */}
        <div className="flex-grow min-w-0">
          <h2 className="capitalize font-semibold text-sm group-hover:text-primary transition-colors">
            {artist.name}
          </h2>
          <div className="flex flex-wrap gap-1 mt-1">
            {(artist.genres || []).slice(0, 4).map((g) => (
              <Badge key={g} variant="outline" className="text-[10px] h-4 border-border/50 capitalize">
                {g}
              </Badge>
            ))}
          </div>
          {artist.bio && (
            <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
              {artist.bio}
            </p>
          )}
        </div>

        {/* Label */}
        {artist.labelName && (
          <span className="text-xs text-muted-foreground hidden sm:block flex-shrink-0 truncate max-w-[120px]">
            {artist.labelName}
          </span>
        )}
      </Link>
    );
  }

  // Grid card
  return (
    <Link href={href} className="group block h-full">
      <Card className="h-full overflow-hidden border-border/50 bg-card hover:bg-card/80 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
        <div className="relative aspect-square bg-muted overflow-hidden">
          {slides.length > 0 ? (
            <SlideshowImage images={slides} alt={artist.name} className="group-hover:scale-105 transition-transform duration-500" {...slideshowProps} />
          ) : (
            <GenerativeArtistAvatar name={artist.name} size={300} className="w-full h-full" animate />
          )}
        </div>
        <CardContent className="p-3">
          <h2 className="capitalize font-semibold text-sm group-hover:text-primary transition-colors truncate">
            {artist.name}
          </h2>
          {/* Always one row high, with or without genres, so every card in a
              row is the same height (Andrés 2026-11-01). */}
          <div className="flex gap-1 mt-1.5 h-4 overflow-hidden">
            {(artist.genres || []).slice(0, 2).map((g) => (
              <Badge key={g} variant="outline" className="text-[10px] h-4 border-border/50 capitalize">
                {g}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

const SUPER_ADMIN_EMAILS = ["breakinbreadswe@gmail.com"];

export default function ArtistsPage() {
  const t = useTranslations("artists");
  const tCommon = useTranslations("common");
  const { user, userProfile } = useAuth();

  const isSuperAdmin =
    SUPER_ADMIN_EMAILS.includes((user?.email || "").toLowerCase().trim()) ||
    userProfile?.accountType === "superadmin";

  const sortLabel: Record<SortMode, string> = {
    az: t("sortAZ"),
    za: t("sortZA"),
    newest: t("sortNewest"),
    oldest: tCommon("sortOldest"),
  };

  const [artists, setArtists] = useState<ArtistProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeGenre, setActiveGenre] = useState<string>("all");
  // ?genre=Hip-Hop — coπ's "show me hip hop artists" (Gordon 2026-11-02).
  useUrlParams((sp) => { const g = sp.get("genre"); if (g) setActiveGenre(g); });
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortMode, setSortMode] = useState<SortMode>("az");
  // Sort + view fold away behind the + beside search (Gordon 2026-11-02:
  // the two rows "taking way too much space on a mobile").
  const [controlsOpen, setControlsOpen] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const { artists: data } = await getArtists({ limitCount: 500 });
        setArtists(data);
      } catch (err) {
        console.error("Error loading artists:", err);
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  // Collect unique genres from all loaded artists
  const allGenres = useMemo(() => {
    const set = new Set<string>();
    artists.forEach((a) => (a.genres || []).forEach((g) => set.add(g)));
    return Array.from(set).sort();
  }, [artists]);

  // Client-side filter + sort
  const filtered = useMemo(() => {
    let list = artists;
    if (activeGenre !== "all") {
      const want = activeGenre.toLowerCase();
      list = list.filter((a) => a.genres?.some((g) => g.toLowerCase() === want));
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (a) =>
          a.name?.toLowerCase().includes(term) ||
          a.genres?.some((g) => g.toLowerCase().includes(term)) ||
          a.bio?.toLowerCase().includes(term) ||
          a.labelName?.toLowerCase().includes(term)
      );
    }
    return sortArtists(list, sortMode);
  }, [artists, activeGenre, searchTerm, sortMode]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Users className="h-12 w-12 text-muted-foreground animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Sticky header ── */}
      <div className="border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-[var(--header-height)] z-30">
        {/* Same toolbar as Releases and the browse pages (Andrés 2026-11-01:
            search sat in a different place on each page): title + count,
            then search on the left with sort + view right after it. */}
        <div className="w-full px-page pt-3 pb-2 space-y-2">
          <div className="flex items-center gap-2">
            <Breadcrumbs heading size="lg" items={[{ label: t("title"), icon: Users }]} />
            <span className="text-xs text-muted-foreground shrink-0">
              {filtered.length !== 1 ? t("countPlural", { n: filtered.length }) : t("count", { n: filtered.length })}
            </span>
          </div>

          <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative min-w-0 flex-1 sm:max-w-sm">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="search"
                  placeholder={t("searchArtists")}
                  aria-label={t("searchArtists")}
                  className="pl-9 h-9 bg-secondary/50"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Show/hide sort + view — a dot marks a changed choice while folded away */}
              <Button
                variant={controlsOpen ? "default" : "outline"}
                size="icon"
                className="relative h-9 w-9 shrink-0"
                onClick={() => setControlsOpen((o) => !o)}
                aria-expanded={controlsOpen}
                aria-controls="artists-controls"
                aria-label={controlsOpen ? t("hideControls") : t("showControls")}
                title={controlsOpen ? t("hideControls") : t("showControls")}
              >
                <Plus className={cn("h-4 w-4 transition-transform duration-200", controlsOpen && "rotate-45")} />
                {!controlsOpen && (sortMode !== "az" || viewMode !== "grid") && (
                  <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
                )}
              </Button>
          </div>

          {controlsOpen && (
          <div id="artists-controls" className="flex items-center gap-2">
              {/* Two sort toggles (Gordon 2026-11-02): A → Z flips to Z → A on a
                  second tap, Newest flips to Oldest — so sort + view fit on one row. */}
              {([["az", "za"], ["newest", "oldest"]] as [SortMode, SortMode][]).map(([first, second]) => {
                const active = sortMode === first || sortMode === second;
                const shown = sortMode === second ? second : first;
                return (
                  <Button
                    key={first}
                    variant={active ? "default" : "outline"}
                    size="sm"
                    className="h-9 px-3 text-xs"
                    onClick={() => setSortMode(sortMode === first ? second : first)}
                    aria-pressed={active}
                    data-tap-info={`Sort: ${sortLabel[shown]}`}
                  >
                    {sortLabel[shown]}
                  </Button>
                );
              })}

              {/* View toggle */}
              <div className="flex shrink-0 rounded-md border border-border overflow-hidden h-9">
                <button
                  className={cn(
                    "px-2.5 flex items-center transition-colors",
                    viewMode === "grid" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
                  )}
                  onClick={() => setViewMode("grid")}
                  title={tCommon("gridView")}
                  aria-label={tCommon("gridView")}
                  aria-pressed={viewMode === "grid"}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  className={cn(
                    "px-2.5 flex items-center transition-colors border-l border-border",
                    viewMode === "list" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
                  )}
                  onClick={() => setViewMode("list")}
                  title={tCommon("listView")}
                  aria-label={tCommon("listView")}
                  aria-pressed={viewMode === "list"}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
          </div>
          )}
        </div>
      </div>

      {/* ── Genre filter strip ── */}
      <div className="w-full px-page py-3">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <Button
            variant={activeGenre === "all" ? "default" : "outline"}
            size="sm"
            className="whitespace-nowrap h-8 text-xs"
            onClick={() => setActiveGenre("all")}
            data-tap-info="Show all genres"
          >
            {tCommon("all")}
          </Button>
          {allGenres.map((genre) => (
            <Button
              key={genre}
              variant={activeGenre === genre ? "default" : "outline"}
              size="sm"
              className="whitespace-nowrap h-8 text-xs capitalize"
              onClick={() => setActiveGenre(genre)}
              data-tap-info={`Genre: ${genre}`}
            >
              {genre}
            </Button>
          ))}
        </div>
      </div>

      {/* ── Artist grid / list ── */}
      <div className="w-full px-3 sm:px-4 pb-12">
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <Users className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">{t("noArtistsFound")}</p>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filtered.map((artist) =>
              isSuperAdmin ? (
                <AdminEditOverlay
                  key={artist.id}
                  entityType="Artist"
                  entityId={artist.id}
                  entityData={artist as unknown as Record<string, unknown>}
                >
                  <ArtistCard artist={artist} viewMode="grid" />
                </AdminEditOverlay>
              ) : (
                <ArtistCard key={artist.id} artist={artist} viewMode="grid" />
              )
            )}
          </div>
        ) : (
          <div className="space-y-2 max-w-3xl">
            {filtered.map((artist) =>
              isSuperAdmin ? (
                <AdminEditOverlay
                  key={artist.id}
                  entityType="Artist"
                  entityId={artist.id}
                  entityData={artist as unknown as Record<string, unknown>}
                >
                  <ArtistCard artist={artist} viewMode="list" />
                </AdminEditOverlay>
              ) : (
                <ArtistCard key={artist.id} artist={artist} viewMode="list" />
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
